<template>
  <div class="profile-relationship-network">
    <div v-if="loadError" class="profile-network-warning">
      {{ loadError }}
    </div>

    <div class="profile-network-header">
      <div class="profile-network-copy">
        <div class="profile-network-title">Relationship map</div>
        <div class="profile-network-subtitle">
          Direct connection neighborhood around this profile
        </div>
      </div>
      <div class="profile-network-pill">
        {{ directRows.length }} direct
      </div>
    </div>

    <relationship-network-map
      :key="mapKey"
      :scoped-rows="profileRelationshipRows"
      :all-rows="relationshipRows"
      selected-attribute="happiness"
      link-mode="all_relational_data"
      node-display-mode="person_team"
      :focused-email="activeEmail"
      :blur-mode="blurMode"
      :loading="mapLoading"
      :show-toolbar="false"
      :show-hud="false"
      :show-attribute-legend="false"
      :show-node-panel="false"
      :include-connected-outside-scope="false"
      :lock-focused-node="true"
      :show-edge-flow="false"
      layout-mode="ego"
      :max-edges="140"
      :height="560"
      loading-title="Loading relationship neighborhood"
      loading-subtitle="Preparing this person's connection map."
      empty-title="No relationship map yet"
      empty-subtitle="This person does not have enough relationship data to show a neighborhood."
    />
  </div>
</template>

<script>
const RelationshipNetworkMap = () => import(/* webpackChunkName: "relationship-network-map" */ '@/components/insights/RelationshipNetworkMap.vue')

function normalizeEmail(value) {
  return String(value || '').replace(/\|\|/g, '.').trim()
}

function normalizeEmailKey(value) {
  return String(value || '').replace(/\./g, '||').trim()
}

function memberEmail(value) {
  if (!value) return ''
  if (typeof value === 'string') return normalizeEmail(value)
  return normalizeEmail(value.email || value.emailKey)
}

export default {
  name: 'ProfileRelationshipNetwork',
  components: {
    RelationshipNetworkMap,
  },
  props: {
    userDetail: {
      type: Object,
      default: () => ({}),
    },
    memberData: {
      type: Object,
      default: () => ({}),
    },
    blurMode: {
      type: Boolean,
      default: false,
    },
    maxNeighbors: {
      type: Number,
      default: 22,
    },
  },
  data() {
    return {
      activeEmail: '',
      profileRows: [],
      loadingRelationships: false,
      loadError: '',
    }
  },
  computed: {
    legacyRelationshipData() {
      return this.memberData && this.memberData.relationships
        ? this.memberData.relationships
        : {}
    },
    profileEmail() {
      return normalizeEmail(
        this.userDetail && (this.userDetail.email || this.userDetail.emailKey)
        || this.legacyRelationshipData.center
        || this.memberData.email
        || this.memberData.emailKey
      )
    },
    relationshipRows() {
      const relationships = this.$store.state.relationships
      const rows = relationships && relationships.dataAllAttribute
      if (Array.isArray(rows) && rows.length > 0) return rows
      return this.profileRows
    },
    rowsByEmail() {
      const lookup = {}
      this.relationshipRows.forEach(row => {
        const email = normalizeEmail(row.email || row.emailKey)
        if (email) lookup[email] = row
      })
      return lookup
    },
    rowsByEmailKey() {
      const lookup = {}
      this.relationshipRows.forEach(row => {
        const key = normalizeEmailKey(row.emailKey || row.email)
        if (key) lookup[key] = row
      })
      return lookup
    },
    activeRow() {
      return this.rowsByEmail[this.activeEmail] || null
    },
    directRows() {
      const row = this.activeRow
      if (!row) return []

      const rows = []
      const seen = {}
      const pushRow = person => {
        const email = normalizeEmail(person && (person.email || person.emailKey))
        if (!email || email === this.activeEmail || seen[email]) return
        seen[email] = true
        rows.push(person)
      }

      if (Array.isArray(row.totalArrData) && row.totalArrData.length > 0) {
        row.totalArrData.forEach(pushRow)
      } else if (Array.isArray(row.totalMembers)) {
        row.totalMembers.forEach(member => {
          const email = memberEmail(member)
          const key = normalizeEmailKey(email || member)
          const person = this.rowsByEmail[email] || this.rowsByEmailKey[key]
          if (person) pushRow(person)
        })
      }

      const strongEmails = new Set(
        []
          .concat(Array.isArray(row.strongMembers) ? row.strongMembers : [])
          .concat(Array.isArray(row.strongArrData) ? row.strongArrData : [])
          .map(memberEmail)
          .filter(Boolean)
      )

      return rows
        .sort((a, b) => {
          const aEmail = normalizeEmail(a.email || a.emailKey)
          const bEmail = normalizeEmail(b.email || b.emailKey)
          const strongDelta = Number(strongEmails.has(bEmail)) - Number(strongEmails.has(aEmail))
          if (strongDelta !== 0) return strongDelta
          const totalDelta = Number(b.total_count || 0) - Number(a.total_count || 0)
          if (totalDelta !== 0) return totalDelta
          return String(a.fullName || '').localeCompare(String(b.fullName || ''))
        })
        .slice(0, this.maxNeighbors)
    },
    profileRelationshipRows() {
      if (!this.activeRow) return []
      return [this.activeRow].concat(this.directRows)
    },
    mapLoading() {
      return this.loadingRelationships && this.relationshipRows.length === 0
    },
    mapKey() {
      return `${this.activeEmail || 'empty'}-${this.profileRelationshipRows.length}-${this.loadingRelationships ? 'loading' : 'ready'}`
    },
  },
  watch: {
    profileEmail: {
      immediate: true,
      handler(value) {
        this.activeEmail = value || ''
        this.profileRows = []
        this.ensureRelationshipData()
      },
    },
    relationshipRows() {
      if (!this.activeEmail && this.profileEmail) {
        this.activeEmail = this.profileEmail
      }
    },
  },
  mounted() {
    this.ensureRelationshipData()
  },
  methods: {
    async ensureRelationshipData() {
      if (this.relationshipRows.length > 0 || this.loadingRelationships) return

      const user = this.$store.state.authen && this.$store.state.authen.user
      if (!user || !user.company || !this.profileEmail) return

      this.loadingRelationships = true
      this.loadError = ''
      try {
        const rows = await this.$store.dispatch('relationships/getProfileNetworkData', {
          user,
          email: this.profileEmail,
          maxNeighbors: this.maxNeighbors,
          profileUser: this.userDetail && Object.keys(this.userDetail).length > 0
            ? this.userDetail
            : this.memberData,
        })
        this.profileRows = Array.isArray(rows) ? rows : []
      } catch (error) {
        this.loadError = 'Unable to load relationship data for this profile.'
      } finally {
        this.loadingRelationships = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-relationship-network {
  width: 100%;
  max-width: 100%;
  margin: 12px 0 0;
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
}

.profile-network-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 28px 12px;
}

.profile-network-copy {
  min-width: 0;
}

.profile-network-title {
  color: rgba(0, 0, 0, 0.86);
  font-size: 15px;
  font-weight: 900;
  line-height: 20px;
}

.profile-network-subtitle {
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.44);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.profile-network-pill {
  flex: none;
  min-height: 28px;
  padding: 0 11px;
  border: 1px solid rgba(95, 50, 167, 0.12);
  border-radius: 999px;
  color: #5f32a7;
  background: rgba(95, 50, 167, 0.07);
  font-size: 12px;
  font-weight: 900;
  line-height: 28px;
  white-space: nowrap;
}

.profile-network-warning {
  margin-top: 10px;
  padding: 9px 11px;
  border: 1px solid #F4DDA3;
  border-radius: 8px;
  background: #FFF8E5;
  color: #7A5200;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.profile-relationship-network ::v-deep .relationship-network-map {
  margin-top: 0;
  border-right: 0;
  border-left: 0;
  border-color: #E4E7EE;
  border-radius: 0;
  box-shadow: none;
}

.profile-relationship-network ::v-deep .map-stage-wrap {
  background-position: center;
  background-size: 38px 38px;
}

.profile-relationship-network ::v-deep .focused-node-anchor {
  z-index: 5;
}

.profile-relationship-network ::v-deep .focused-node-avatar {
  width: 54px;
  height: 54px;
}

@media (max-width: 560px) {
  .profile-network-header {
    align-items: flex-start;
    flex-direction: column;
    padding-right: 18px;
    padding-left: 18px;
  }
}
</style>
